import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import GlassmorphismTokens from '../../styles/GlassmorphismTokens';
import { BlueCTAButton } from '../shared/GlassCard';
import { PSYCHOGRAPHIC_DIMENSIONS, TRAVEL_SOUL_LABELS } from '../../data/psychographicData';

const Wrapper = styled.div`
  max-width: 480px;
  width: 100%;
  padding: 32px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: auto;
  max-height: 100vh;
`;

const Title = styled(motion.h1)`
  font-size: 28px;
  font-weight: 800;
  color: white;
  text-align: center;
  margin: 0 0 8px 0;
`;

const PersonalityBadge = styled(motion.div)`
  display: inline-block;
  padding: 8px 20px;
  border-radius: 9999px;
  background: ${GlassmorphismTokens.gradient.blueCTA};
  color: white;
  font-size: 16px;
  font-weight: 700;
  box-shadow: ${GlassmorphismTokens.shadow.blueCTA};
  margin-bottom: 24px;
`;

const SoulSection = styled(motion.div)`
  width: 100%;
  background: ${GlassmorphismTokens.glass.medium};
  backdrop-filter: ${GlassmorphismTokens.blur.lg};
  -webkit-backdrop-filter: ${GlassmorphismTokens.blur.lg};
  border: ${GlassmorphismTokens.border.medium};
  border-radius: 20px;
  padding: 20px;
  margin-bottom: 24px;
  text-align: center;

  @supports not (backdrop-filter: blur(1px)) {
    background: rgba(40, 40, 60, 0.85);
  }
`;

const SoulLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #4285F4;
  margin-bottom: 8px;
`;

const SoulTitle = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: white;
  margin-bottom: 4px;
`;

const SoulDescription = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  margin: 0;
  line-height: 1.5;
`;

/* ─── Radar Chart (reused pattern from TasteProfileCard) ─── */
const CHART_SIZE = 200;
const CENTER = CHART_SIZE / 2;
const RADIUS = 75;
const RINGS = 4;
const DNA_DIMS = [
  { key: 'culture', label: 'Culture' },
  { key: 'adventure', label: 'Adventure' },
  { key: 'relaxation', label: 'Relaxation' },
  { key: 'food', label: 'Food' },
  { key: 'nature', label: 'Nature' },
  { key: 'social', label: 'Social' },
];

function polarToCartesian(angle, radius) {
  const rad = (angle - 90) * (Math.PI / 180);
  return { x: CENTER + radius * Math.cos(rad), y: CENTER + radius * Math.sin(rad) };
}

const ChartContainer = styled(motion.div)`
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
`;

/* ─── Psychographic Spectrum Bars ─── */
const BarsSection = styled(motion.div)`
  width: 100%;
  background: ${GlassmorphismTokens.glass.medium};
  backdrop-filter: ${GlassmorphismTokens.blur.lg};
  -webkit-backdrop-filter: ${GlassmorphismTokens.blur.lg};
  border: ${GlassmorphismTokens.border.medium};
  border-radius: 20px;
  padding: 20px;
  margin-bottom: 32px;

  @supports not (backdrop-filter: blur(1px)) {
    background: rgba(40, 40, 60, 0.85);
  }
`;

const BarsSectionTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #4285F4;
  margin-bottom: 16px;
`;

const BarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const BarLabel = styled.span`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
  width: 80px;
  text-align: ${props => props.$align || 'right'};
  flex-shrink: 0;

  @media (max-width: 400px) {
    width: 60px;
    font-size: 10px;
  }
`;

const BarTrack = styled.div`
  flex: 1;
  height: 6px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 9999px;
  overflow: hidden;
  position: relative;
`;

const BarFill = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background: ${GlassmorphismTokens.gradient.blueCTA};
  border-radius: 9999px;
`;

const CTAWrapper = styled.div`
  width: 100%;
  max-width: 320px;
`;

const SwipeResultReveal = ({ dimensions, personality, psychographic, travelSoul, onStartPlanning }) => {
  const soulData = TRAVEL_SOUL_LABELS[travelSoul] || TRAVEL_SOUL_LABELS.wanderer;

  // Radar chart points
  const radarPoints = DNA_DIMS.map((dim, i) => {
    const angle = (360 / DNA_DIMS.length) * i;
    const value = dimensions[dim.key] || 0;
    return polarToCartesian(angle, RADIUS * value);
  });
  const polygonPoints = radarPoints.map(p => `${p.x},${p.y}`).join(' ');

  return (
    <Wrapper>
      <Title
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        Your Travel DNA
      </Title>

      <PersonalityBadge
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.3, type: 'spring' }}
      >
        {personality}
      </PersonalityBadge>

      {/* Radar Chart */}
      <ChartContainer
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.5, duration: 0.5 }}
      >
        <svg width={CHART_SIZE} height={CHART_SIZE} viewBox={`0 0 ${CHART_SIZE} ${CHART_SIZE}`}>
          {Array.from({ length: RINGS }, (_, i) => {
            const r = (RADIUS / RINGS) * (i + 1);
            const pts = DNA_DIMS.map((_, j) => polarToCartesian((360 / DNA_DIMS.length) * j, r));
            return (
              <polygon key={i} points={pts.map(p => `${p.x},${p.y}`).join(' ')} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
            );
          })}
          {DNA_DIMS.map((_, i) => {
            const end = polarToCartesian((360 / DNA_DIMS.length) * i, RADIUS);
            return <line key={i} x1={CENTER} y1={CENTER} x2={end.x} y2={end.y} stroke="rgba(255,255,255,0.08)" strokeWidth="1" />;
          })}
          <motion.polygon
            points={polygonPoints}
            fill="rgba(66, 133, 244, 0.2)"
            stroke="#4285F4"
            strokeWidth="2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7, duration: 0.6 }}
          />
          {radarPoints.map((p, i) => (
            <motion.circle key={i} cx={p.x} cy={p.y} r="3.5" fill="#4285F4" stroke="white" strokeWidth="1.5"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 + i * 0.08 }}
            />
          ))}
          {DNA_DIMS.map((dim, i) => {
            const pos = polarToCartesian((360 / DNA_DIMS.length) * i, RADIUS + 20);
            return (
              <text key={dim.key} x={pos.x} y={pos.y} textAnchor="middle" dominantBaseline="middle"
                fill="rgba(255,255,255,0.6)" fontSize="10" fontWeight="500"
              >
                {dim.label}
              </text>
            );
          })}
        </svg>
      </ChartContainer>

      {/* Travel Soul */}
      <SoulSection
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1 }}
      >
        <SoulLabel>Your Travel Soul</SoulLabel>
        <SoulTitle>{soulData.label}</SoulTitle>
        <SoulDescription>{soulData.description}</SoulDescription>
      </SoulSection>

      {/* Psychographic Bars */}
      {psychographic && (
        <BarsSection
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2 }}
        >
          <BarsSectionTitle>Your Travel Style</BarsSectionTitle>
          {PSYCHOGRAPHIC_DIMENSIONS.map((dim, i) => (
            <BarRow key={dim.key}>
              <BarLabel>{dim.labelLow}</BarLabel>
              <BarTrack>
                <BarFill
                  initial={{ width: 0 }}
                  animate={{ width: `${psychographic[dim.key] || 50}%` }}
                  transition={{ delay: 1.4 + i * 0.1, duration: 0.6 }}
                />
              </BarTrack>
              <BarLabel $align="left">{dim.labelHigh}</BarLabel>
            </BarRow>
          ))}
        </BarsSection>
      )}

      <CTAWrapper>
        <BlueCTAButton onClick={onStartPlanning} whileTap={{ scale: 0.97 }}>
          <span>Start Planning</span>
          <svg viewBox="0 0 20 20" fill="currentColor" width="20" height="20">
            <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </BlueCTAButton>
      </CTAWrapper>
    </Wrapper>
  );
};

export default SwipeResultReveal;
